// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, Input, OnChanges, OnInit, ViewChild, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { HceService } from '../../../services/hce/hce.service';
import { Analysis } from '../../../interfaces/analysis';
import { SvResponse, GeneLink, SvListTableData } from '../../../interfaces/hce/hce-sv';
import { mapSvResponseToTableRows } from '../../../shared/hce/mapper/sv-list-mapper';
import { QualityScoreMax4Pipe } from '../../../shared/hce/pipe/quality-score-max4.pipe';
import { GenesListDialogComponent } from '../../../shared/hce/genes-list-dialog/genes-list-dialog.component';
import { HceAnnotation } from '../../../interfaces/hce/hce-annotation';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WithAnnotTooltipDirective } from '../../../shared/hce/annot-tooltip/annot-tooltip.directive';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { IconsComponent } from '../../../shared/icons/icons.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

type AnnotColumns = 'withAnnotation';
type ActionColumns = 'actions';
type ColumnKey = keyof SvListTableData | AnnotColumns | ActionColumns;
type MatchedAnnot = {
  file: string;
  chromosome: string;
  svid?: string;
  type?: string;
  start: number;
  end: number;
  length?: number;
  svConSize?: number;
  svRefSize?: number;
  zygosity?: string;
  color?: string;
  overlap?: number;
  ratio?: number;
};

@Component({
  selector: 'app-hce-sv-list-view',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatProgressBarModule,
    MatPaginatorModule,
    QualityScoreMax4Pipe,
    MatButtonModule,
    MatDialogModule,
    MatSortModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    WithAnnotTooltipDirective,
    IconsComponent,
    MatProgressSpinnerModule
  ],
  templateUrl: './hce-sv-list-view.component.html',
  styleUrls: ['./hce-sv-list-view.component.css'],
})
export class HceSvListViewComponent implements OnInit, OnChanges {

  @Input({ required: true }) analysis!: Analysis;

  // Rebuild the table every time sv changes.
  private _sv!: SvResponse;
  @Input({ required: true })
  set sv(value: SvResponse) {
    this._sv = value;
    this.applySv(value);
  }
  get sv(): SvResponse { return this._sv; }

  @Input() selectedAnnotationByFile: Record<string, HceAnnotation[]> = {};
  @Input() selectedAnnotationFiles: string[] = [];
  trackByFilename = (_: number, filename: string) => filename;

  private annotIndex: Record<string, Record<string, HceAnnotation[]>> = {};
  private withAnnotBySvId = new Map<string, MatchedAnnot[]>();
  private matchCountBySvId = new Map<string, number>();

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  private _sort!: MatSort;
  @ViewChild(MatSort, { static: false })
  set sort(matSort: MatSort) {
    this._sort = matSort;
    if (this._sort) {
      this.setupSortingWith(this._sort);
    }
  }

  sex: string | null = null;
  variantsCount = 0;
  geneCount = 0;
  private countTranslocationTo = false;
  private uniqueKeyMode: 'name+id' | 'name' | 'id' = 'name+id';

  UCSC_REF_VER = 'hg38';
  isLoading = true;

  @Input() refNameMap: Record<string, string> = {};
  chrOrder: string[] = [];
  reverseChrNameMap: Record<string, string> = {};

  dataSource = new MatTableDataSource<SvListTableData>([]);
  displayedColumns: ColumnKey[] = [
    'svId',
    'chrId',
    'start',
    'end',
    'length',
    'chrToId',
    'startTo',
    'endTo',
    'lengthTo',
    'type',
    'zygosity',
    'flaggedSv',
    'genename',
    'genenameTo',
    'withAnnotation',
    'minimumIntervalDepth',
    'medianIntervalDepth',
    'qualityScore',
    'reason',
    'molCheck',
    'colorSdb',
    'gnomAd',
    'ontLrs',
    'masked',
    'bestSvPerCluster',
    'actions',
  ];

  constructor(
    private hce: HceService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    const analysis_id = (this.analysis as any).AnalysisID;
    const panelId = (this.analysis as any).PanelID ?? (this.analysis as any).panelId;

    if (!analysis_id) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    forkJoin({
      ucsc: panelId ? this.hce.getUcscRefVersion(panelId) : of('hg38'),
      gender: this.hce.getGender<string>(analysis_id),
    }).subscribe({
      next: ({ ucsc, gender }) => {
        this.chrOrder = Object.keys(this.refNameMap);
        this.reverseChrNameMap = Object.fromEntries(
          Object.entries(this.refNameMap).map(([k, v]) => [v.toLowerCase(), k])
        );

        this.UCSC_REF_VER = ucsc ?? 'hg38';

        this.applySv(this.sv);
        this.overwritePaginatorLabel();

        this.sex = gender ?? '-';
        this.isLoading = false;
      },

      error: err => {
        console.error('Init failed:', err);
        this.isLoading = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAnnotationFiles']) {
      this.rebuildAnnotationIndex();
      if ((this.dataSource?.data?.length ?? 0) > 0) {
        this.recomputeWithAnnotation();
      }
    }
  }

  private overwritePaginatorLabel(): void {
    if (!this.paginator) return;
    const intl: MatPaginatorIntl = (this.paginator as any)._intl;
    intl.itemsPerPageLabel = $localize`Rows per page:`;
    intl.nextPageLabel = $localize`Next page`;
    intl.previousPageLabel = $localize`Previous page`;
    intl.firstPageLabel = $localize`First page`;
    intl.lastPageLabel = $localize`Last page`;
    intl.getRangeLabel = (page, pageSize, length) => {
      if (length === 0 || pageSize === 0) return $localize`0 of ${length}`;
      const startIndex = page * pageSize;
      const endIndex = Math.min(startIndex + pageSize, length);
      return $localize`${startIndex + 1} – ${endIndex} of ${length}`;
    };
    intl.changes.next();
  }

  getChrLabel(id: number | null | undefined): string {
    if (id == null) return '';
    const key = String(id);
    return this.refNameMap[key] || `chr${id}`;
  }

  private getChrSortOrder(id: number | null | undefined): number {
    if (id == null) return 9999;
    const key = String(id);
    const idx = this.chrOrder.indexOf(key);
    return idx >= 0 ? idx : 9999;
  }

  private makeCompositeSortKey(r: SvListTableData): string {
    const chrSortOrder = this.getChrSortOrder(r.chrId);

    const padInt = (v: number | null | undefined, n: number) =>
      String(Math.max(0, Math.floor((v ?? 0) * 1000))).padStart(n, '0');

    return (
      String(chrSortOrder).padStart(4, '0') +
      padInt(r.start, 9) +
      padInt(r.end, 9)
    );
  }

  private setupSortingWith(sort: MatSort): void {
    this.dataSource.sort = sort;

    this.dataSource.sortingDataAccessor = (item: SvListTableData, property: string): any => {
      switch (property as keyof SvListTableData | 'withAnnotation') {

        case 'chrId':
          return this.makeCompositeSortKey(item);

        case 'start':
        case 'end':
        case 'length':
        case 'chrToId':
        case 'startTo':
        case 'endTo':
        case 'lengthTo':
        case 'minimumIntervalDepth':
        case 'medianIntervalDepth':
        case 'qualityScore':
        case 'colorSdb':
        case 'gnomAd':
        case 'ontLrs': {
          const v = (item as any)[property] as number | null | undefined;
          return v ?? Number.NEGATIVE_INFINITY;
        }

        case 'molCheck':
        case 'masked':
        case 'bestSvPerCluster': {
          const v = (item as any)[property] as boolean | null | undefined;
          return v == null ? -1 : (v ? 1 : 0);
        }

        case 'genename': {
          const arr = (item.geneLinks ?? [])
            .map(g => (g?.name ?? '').trim().toLowerCase())
            .filter(Boolean);
          return arr.join(',');
        }
        case 'genenameTo': {
          const arr = (item.geneLinksTo ?? [])
            .map(g => (g?.name ?? '').trim().toLowerCase())
            .filter(Boolean);
          return arr.join(',');
        }

        case 'withAnnotation':
          return this.matchCountBySvId.get(item.svId) ?? 0;

        default:
          return (item as any)[property];
      }
    };

  }

  openUCSC(chrId: number | null | undefined, posKb: number | null | undefined): void {
    if (chrId == null || posKb == null) return;

    const pos = this.toBp(posKb);
    if (pos == null) return;

    const chr = this.getChrLabel(chrId);
    if (!chr) return;

    this.hce.openUcscGenomeBrowser(this.UCSC_REF_VER, chr, pos);
  }

  openGenesDialog(titleKey: string, genes: GeneLink[] = []): void {
    this.dialog.open(GenesListDialogComponent, {
      width: '50rem',
      height: '38rem',
      position: { right: '10px' },
      data: {
        title: `SV_ID:${titleKey}`,
        genes,
      }
    });
  }

  openEnsembl(id?: string): void {
    if (!id) return;
    const url = `https://ensembl.org/Homo_sapiens/Gene/Summary?g=${encodeURIComponent(id)}`;
    window.open(url, '_blank', 'noopener');
  }

  private toBp(kb: number | null | undefined): number | null {
    if (kb == null) return null;
    return Math.max(1, Math.floor(kb * 1000));
  }

  private recalcCounts(): void {
    const filtered = this.dataSource.filteredData ?? this.dataSource.data ?? [];
    this.variantsCount = filtered.length;

    const uniq = new Set<string>();
    const pushKey = (name?: string, id?: string) => {
      const nm = (name ?? '').trim();
      const gid = (id ?? '').trim();
      let key: string;
      switch (this.uniqueKeyMode) {
        case 'name': key = nm; break;
        case 'id': key = gid; break;
        default: key = `${nm}\t${gid}`;
      }
      if (key) uniq.add(key);
    };
    for (const row of filtered) {
      if (Array.isArray(row.geneLinks)) {
        for (const g of row.geneLinks) pushKey(g?.name, g?.id);
      }
      if (this.countTranslocationTo && Array.isArray(row.geneLinksTo)) {
        for (const g of row.geneLinksTo) pushKey(g?.name, g?.id);
      }
    }
    this.geneCount = uniq.size;
  }

  private normalizeChr(s: string | null | undefined): string {
    if (!s) return '';
    const lower = s.trim().toLowerCase();

    if (this.reverseChrNameMap[lower]) return this.reverseChrNameMap[lower];
    if (lower.startsWith('chr')) return lower.slice(3);
    return lower;
  }

  private toBpSafe(kb: number | null | undefined): number | null {
    if (kb == null) return null;
    return Math.max(1, Math.floor(kb * 1000));
  }

  private hr(width: number, ch = '─'): string {
    return ch.repeat(Math.max(1, width));
  }

  private fmtType(t?: string | string[] | null): string {
    if (t == null) return '';
    const s = Array.isArray(t) ? t.join(',') : String(t);
    return s ? `(${s})` : '';
  }

  private fmtBpFloor(n?: number | null): string {
    if (n == null) return '-';
    return Math.floor(n).toLocaleString();
  }

  isAnnotated(svId: string): boolean {
    return (this.matchCountBySvId.get(svId) ?? 0) > 0;
  }

  private rebuildAnnotationIndex(): void {
    const idx: typeof this.annotIndex = {};
    for (const [filename, list] of Object.entries(this.selectedAnnotationByFile ?? {})) {
      const byChr: Record<string, HceAnnotation[]> = {};
      for (const rec of list ?? []) {
        const key = this.normalizeChr(rec.mappedRefName);
        if (!key) continue;
        (byChr[key] ||= []).push(rec);
      }
      idx[filename] = byChr;
    }
    this.annotIndex = idx;
  }

  private recomputeWithAnnotation(): void {
    this.withAnnotBySvId.clear();
    this.matchCountBySvId.clear();

    const rows = this.dataSource.data ?? [];
    for (const row of rows) {
      const chrLabel = this.getChrLabel(row.chrId);
      const chrKey = this.normalizeChr(chrLabel);
      const s = this.toBpSafe(row.start);
      const e = this.toBpSafe(row.end);
      if (!chrKey || s == null || e == null) continue;

      const start = Math.min(s, e);
      const end = Math.max(s, e);
      const svLen = Math.max(1, end - start);

      const matches: MatchedAnnot[] = [];
      for (const [filename, chrMap] of Object.entries(this.annotIndex)) {
        const list = chrMap[chrKey] ?? [];
        for (const r of list) {
          const aStart = r.start;
          const aEnd = r.end;
          if (aEnd >= start && aStart <= end) {
            const overlap = Math.min(end, aEnd) - Math.max(start, aStart);
            const ratio = overlap > 0 ? (overlap / svLen) * 100 : 0;
            matches.push({
              file: filename,
              chromosome: r.mappedRefName,
              svid: (r as any).svId ?? (r as any).SVID ?? '',
              type: Array.isArray(r.type) ? r.type.join(',') : (r.type as string | undefined),
              start: aStart,
              end: aEnd,
              length: r.length,
              svConSize: (r as any).sVConSize,
              svRefSize: (r as any).sVRefSize,
              zygosity: (r as any).zygosity ?? (r as any).Zygosity,
              color: (r as any).color,
              overlap,
              ratio,
            });
          }
        }
      }
      if (matches.length) {
        this.withAnnotBySvId.set(row.svId, matches);
        this.matchCountBySvId.set(row.svId, matches.length);
      }
    }

    if (this.dataSource.sort) {
      this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort);
    }
  }

  private applySv(sv: SvResponse): void {
    const rows = mapSvResponseToTableRows(sv);
    rows.sort((a, b) => this.makeCompositeSortKey(a).localeCompare(this.makeCompositeSortKey(b)));
    this.dataSource.data = rows;
    this.dataSource.paginator = this.paginator;

    setTimeout(() => {
      if (this._sort) {
        this._sort.active = 'chrId';
        this._sort.direction = 'asc';
        this._sort.sortChange.emit({ active: 'chrId', direction: 'asc' });
      }
    });

    this.rebuildAnnotationIndex();
    this.recomputeWithAnnotation();
    this.recalcCounts();
    this.cdr.markForCheck();
  }

  buildTooltip(svId: string): string {
    const matches = this.withAnnotBySvId.get(svId) ?? [];
    if (matches.length === 0) return '';

    const first = matches[0];
    const out: string[] = [];

    if (first?.chromosome) out.push(first.chromosome);

    const groups = new Map<string, MatchedAnnot[]>();
    for (const m of matches) {
      if (!groups.has(m.file)) groups.set(m.file, []);
      groups.get(m.file)!.push(m);
    }

    const entries = Array.from(groups.entries());
    const lastIndex = entries.length - 1;
    let globalMaxWidth = 0;

    for (const [, arr] of entries) {
      const svLines = arr.map(a => {
        const svid = a.svid ? `SVID:${a.svid}` : 'SVID:';
        const type = this.fmtType(a.type);
        const dispStart = this.fmtBpFloor(a.start);
        const dispSv = this.fmtBpFloor(a.length);
        const dispRef = this.fmtBpFloor(a.svRefSize);
        const dispCon = this.fmtBpFloor(a.svConSize);

        return (
          `${svid}${type}@${dispStart}bp` +
          `/SvLen:${dispSv}bp` +
          `/RefLen:${dispRef}bp` +
          `/ConLen:${dispCon}bp` +
          `/Quality score:` +
          `/Reason:`
        );
      });

      const maxWidth = svLines.reduce((mx, line) => {
        const len = line.trimEnd().length;
        return Math.max(mx, len);
      }, 0);

      if (maxWidth > globalMaxWidth) globalMaxWidth = maxWidth;
    }

    entries.forEach(([file, arr], index) => {
      out.push(file);
      out.push(this.hr(globalMaxWidth));

      const svLines = arr.map(a => {
        const svid = a.svid ? `SVID:${a.svid}` : 'SVID:';
        const type = this.fmtType(a.type);
        const dispStart = this.fmtBpFloor(a.start);
        const dispSv = this.fmtBpFloor(a.length);
        const dispRef = this.fmtBpFloor(a.svRefSize);
        const dispCon = this.fmtBpFloor(a.svConSize);

        return (
          `${svid}${type}@${dispStart}bp` +
          `/SvLen:${dispSv}bp` +
          `/RefLen:${dispRef}bp` +
          `/ConLen:${dispCon}bp` +
          `/Quality score:` +
          `/Reason:`
        );
      });

      svLines.forEach((line, idx) => {
        out.push(line);
        if (idx < svLines.length - 1) {
          out.push(this.hr(globalMaxWidth));
        }
      });

      if (index < lastIndex) {
        out.push('');
        out.push('');
      }
    });

    return out.join('\n');
  }

  openAlignmentView(row: SvListTableData): void {
    const analysisId = (this.analysis as any)?.AnalysisID;
    if (!analysisId) return;

    const db = this.UCSC_REF_VER || 'hg38';
    const chromosomeName = this.getChrLabel(row.chrId);
    if (!chromosomeName) return;

    const startBp = this.toBp(row.start);
    const endBp = this.toBp(row.end);
    if (startBp == null || endBp == null) return;

    const bp = Math.floor((startBp + endBp) / 2);
    const range = '3';
    const alignmentScale = 'scale1';
    const annotationFile = this.selectedAnnotationFiles?.length ? this.selectedAnnotationFiles : [];
    const contig = row.type === 'Translocation' && row.contig ? row.contig : '';

    const payload = {
      analysisId: String(analysisId),
      svId: row.svsKey,
      db,
      bp: String(bp),
      range,
      alignmentScale,
      chromosomeName,
      annotationFile,
      contig,
    };

    this.hce.openAlignmentViewer(payload, '_blank');
  }

}